// src/lib/products/images.ts

import { siteConfig } from "~/lib/config/site"
import type { ProductSlug } from "~/server/stripe/checkout"

/**
 * @fileoverview Storefront product image registry and URL helpers.
 * @description Two image families live here. Know which is wired where:
 *
 * **LIVE — static square `.product` webp (your 1×1 assets)**
 * - `/merchant-feed.xml` → `g:image_link` via `PRODUCT_CATALOG.imagePath`
 * - Product JSON-LD → `image`
 *
 * **LIVE — static `.og` webp via `opengraph-image` route (Satori PNG fallback)**
 * - Page `<meta property="og:image">` / `twitter:image` (colocated route serves
 *   the 2400×1260 webp when on disk; otherwise the existing Satori card)
 * - WebPage JSON-LD → `primaryImageOfPage` (same `/opengraph-image` URL)
 *
 * @module products/images
 */

/**
 * Which storefront surfaces consume which image source.
 *
 * @typedef {Object} StorefrontImageWiring
 * @property {string} merchantFeed - Image source for `g:image_link`.
 * @property {string} productJsonLd - Image source for Product `image`.
 * @property {string} socialMeta - Image source for `og:image` / `twitter:image`.
 * @property {string} webPageJsonLd - Image source for WebPage `primaryImageOfPage`.
 * @property {string} staticOgWebp - Availability of the 2400×1260 static OG webp.
 */
export const STOREFRONT_IMAGE_WIRING = {
  merchantFeed: "static-product-webp",
  productJsonLd: "static-product-webp",
  socialMeta: "static-og-route-with-satori-fallback",
  webPageJsonLd: "static-og-route-with-satori-fallback",
  staticOgWebp: "static-og-route-with-satori-fallback",
} as const

/**
 * Image bundle for a sellable storefront SKU.
 *
 * @typedef {Object} ProductImageSet
 * @property {string} product - LIVE: square webp for Merchant Center + Product JSON-LD.
 * @property {number} productWidth - Product image width in pixels.
 * @property {number} productHeight - Product image height in pixels.
 * @property {string} og - LIVE: 1.91:1 static OG webp served by the opengraph route.
 * @property {number} ogWidth - OG image width in pixels.
 * @property {number} ogHeight - OG image height in pixels.
 */
export interface ProductImageSet {
  product: string
  productWidth: number
  productHeight: number
  og: string
  ogWidth: number
  ogHeight: number
}

/** Per-SKU storefront artwork keyed by checkout slug. */
export const PRODUCT_IMAGES = {
  "ads-framework": {
    product:
      "/images/storefront/ads-framework/adsights-ads-framework-product-image-1x1-2048x2048_2x.webp",
    productWidth: 2048,
    productHeight: 2048,
    og: "/images/storefront/ads-framework/adsights-ads-framework-og-image_AR-1.91-1_2400x1260_2x.webp",
    ogWidth: 2400,
    ogHeight: 1260,
  },
  "ads-skills-only": {
    product:
      "/images/storefront/ads-skills-only/adsights-ads-skills-for-claude-product-image-1x1-2048x2048_2x.webp",
    productWidth: 2048,
    productHeight: 2048,
    og: "/images/storefront/ads-skills-only/adsights-ads-skills-for-claude-og-image_AR-1.91-1_2400x1260_2x.webp",
    ogWidth: 2400,
    ogHeight: 1260,
  },
  "email-framework": {
    product:
      "/images/storefront/email-framework/adsights-email-framework-product-image-1x1-2048x2048_2x.webp",
    productWidth: 2048,
    productHeight: 2048,
    og: "/images/storefront/email-framework/adsights-email-framework-og-image_AR-1.91-1_2400x1260_2x.webp",
    ogWidth: 2400,
    ogHeight: 1260,
  },
  "email-skills-only": {
    product:
      "/images/storefront/email-skills-only/adsights-email-skills-for-claude-product-image-1x1-2048x2048_2x.webp",
    productWidth: 2048,
    productHeight: 2048,
    og: "/images/storefront/email-skills-only/adsights-email-skills-for-claude-og-image_AR-1.91-1_2400x1260_2x.webp",
    ogWidth: 2400,
    ogHeight: 1260,
  },
} as const satisfies Record<ProductSlug, ProductImageSet>

/**
 * Resolve the canonical image bundle for a storefront SKU.
 *
 * @param {ProductSlug} id - Stable SKU slug matching checkout and the feed.
 * @returns {ProductImageSet} Square product and reserved OG artwork paths.
 */
export function getProductImages(id: ProductSlug): ProductImageSet {
  return PRODUCT_IMAGES[id]
}

/**
 * Site-relative path to the LIVE square product webp for a SKU.
 *
 * @param {ProductSlug} id - Stable SKU slug.
 * @returns {string} Path under `/images/storefront/…`.
 */
export function staticProductImagePath(id: ProductSlug): string {
  return PRODUCT_IMAGES[id].product
}

/**
 * Site-relative path to the static OG webp for a SKU.
 *
 * @param {ProductSlug} id - Stable SKU slug.
 * @returns {string} Path under `/images/storefront/…` (served by the opengraph route).
 */
export function staticOgImagePath(id: ProductSlug): string {
  return PRODUCT_IMAGES[id].og
}

/**
 * Absolute URL for the LIVE square product webp (Merchant Center + Product JSON-LD).
 *
 * @param {ProductSlug} id - Stable SKU slug.
 * @returns {string} Absolute https URL on the canonical origin.
 */
export function absoluteStaticProductImageUrl(id: ProductSlug): string {
  return absoluteStorefrontImageUrl(staticProductImagePath(id))
}

/**
 * Absolute URL for the static OG webp file (direct public path; social uses the route).
 *
 * @param {ProductSlug} id - Stable SKU slug.
 * @returns {string} Absolute https URL on the canonical origin.
 */
export function absoluteStaticOgImageUrl(id: ProductSlug): string {
  return absoluteStorefrontImageUrl(staticOgImagePath(id))
}

/**
 * Absolute URL for the opengraph-image route (static webp primary, Satori fallback).
 *
 * @param {string} pagePath - Site-relative product page path, e.g. `/frameworks/ads-framework`.
 * @returns {string} Absolute https URL ending in `/opengraph-image`.
 */
export function satoriOpenGraphRouteUrl(pagePath: string): string {
  const normalized = pagePath.startsWith("/") ? pagePath : `/${pagePath}`
  return `${siteConfig.url}${normalized}/opengraph-image`
}

/**
 * Resolve a site-relative storefront image path to an absolute URL.
 *
 * @param {string} path - Site-relative path beginning with `/images/storefront/…`.
 * @returns {string} Absolute https URL on the canonical origin.
 */
export function absoluteStorefrontImageUrl(path: string): string {
  const normalized = path.startsWith("/") ? path : `/${path}`
  return `${siteConfig.url}${normalized}`
}
